import { useState } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'

interface AdminMfaResetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: { id: string; email: string } | null
  onReset?: () => void
}

export function AdminMfaResetDialog({ open, onOpenChange, user, onReset }: AdminMfaResetDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    if (!user) return
    setIsLoading(true)
    setError(null)
    try {
      await api.post(`/admin/users/${user.id}/reset-mfa`, {})
      toast.success(`MFA reset for ${user.email}. They'll be prompted to re-enroll on next sign-in.`)
      onReset?.()
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset MFA')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset MFA for {user?.email ?? 'this user'}?</DialogTitle>
          <DialogDescription>
            This will remove their authenticator and all recovery codes. They will need to
            re-enroll on next sign-in. Only do this after verifying their identity out-of-band
            (phone call, video call, signed letter).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              className="hover:text-destructive"
              onClick={() => void handleConfirm()}
              disabled={isLoading || !user}
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Reset MFA
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
